import { createEnum } from "@redotech/zod/zod-enum";
import { PillTheme } from "./pill-theme";
import { reverseRecord } from "./records-utils";
export enum ReturnStatus {
  OPEN = "open",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  NEEDS_REVIEW = "needs_review",
  IN_REVIEW = "in_review",
  COMPLETE = "complete",
  REJECTED = "rejected",
  FLAGGED = "flagged",
  PRE_SHIPMENT = "pre-shipment",
  DELETED = "deleted",
}
export const ReturnStatusZod = createEnum(ReturnStatus);

export const returnStatusToDisplayText: Record<ReturnStatus, string> = {
  [ReturnStatus.COMPLETE]: "Complete",
  [ReturnStatus.DELIVERED]: "Delivered",
  [ReturnStatus.IN_TRANSIT]: "In transit",
  [ReturnStatus.NEEDS_REVIEW]: "Needs review",
  [ReturnStatus.IN_REVIEW]: "In review",
  [ReturnStatus.OPEN]: "Open",
  [ReturnStatus.REJECTED]: "Rejected",
  [ReturnStatus.FLAGGED]: "Flagged",
  [ReturnStatus.PRE_SHIPMENT]: "Pre-shipment",
  [ReturnStatus.DELETED]: "Reset",
};

export const isReturnStatusDisplayedRejected: Record<ReturnStatus, boolean> = {
  [ReturnStatus.OPEN]: false,
  [ReturnStatus.NEEDS_REVIEW]: false,
  [ReturnStatus.IN_TRANSIT]: false,
  [ReturnStatus.DELIVERED]: false,
  [ReturnStatus.COMPLETE]: false,
  [ReturnStatus.IN_REVIEW]: false,
  [ReturnStatus.FLAGGED]: false,
  [ReturnStatus.PRE_SHIPMENT]: false,
  [ReturnStatus.REJECTED]: true,
  [ReturnStatus.DELETED]: true,
};

export const inferredReturnStatusFromDisplayText = reverseRecord(
  returnStatusToDisplayText,
);

export const returnStatusToBadgeColor: Record<ReturnStatus, PillTheme> = {
  [ReturnStatus.COMPLETE]: PillTheme.SUCCESS,
  [ReturnStatus.DELIVERED]: PillTheme.SUCCESS,
  [ReturnStatus.IN_TRANSIT]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.NEEDS_REVIEW]: PillTheme.NEUTRAL_YELLOW,
  [ReturnStatus.IN_REVIEW]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.OPEN]: PillTheme.NEUTRAL_YELLOW,
  [ReturnStatus.REJECTED]: PillTheme.DANGER,
  [ReturnStatus.FLAGGED]: PillTheme.DANGER,
  [ReturnStatus.PRE_SHIPMENT]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.DELETED]: PillTheme.NEUTRAL_YELLOW,
};

export function shouldSkipAutomaticProcessing(
  status: ReturnStatus,
  ignoreFlagged: boolean = false,
) {
  return (
    isClosedReturnStatus(status) ||
    [
      ReturnStatus.NEEDS_REVIEW,
      ...(ignoreFlagged ? [] : [ReturnStatus.FLAGGED]),
    ].includes(status)
  );
}

export const closedReturnStatuses = [
  ReturnStatus.COMPLETE,
  ReturnStatus.DELETED,
  ReturnStatus.REJECTED,
];

export function isClosedReturnStatus(status: ReturnStatus) {
  return closedReturnStatuses.includes(status);
}

export function isCancelledReturnStatus(status: ReturnStatus) {
  return [ReturnStatus.DELETED, ReturnStatus.REJECTED].includes(status);
}

export function pillThemeToBadgeColor(
  theme: PillTheme,
): "success" | "blue" | "warning" | "error" | "gray" {
  switch (theme) {
    case PillTheme.SUCCESS:
      return "success";
    case PillTheme.NEUTRAL_BLUE:
      return "blue";
    case PillTheme.NEUTRAL_YELLOW:
      return "warning";
    case PillTheme.DANGER:
      return "error";
    default:
      return "gray";
  }
}
